// src/pages/biz/ResponsePage.js
// 运营中心 · 响应管理（doc01 2.19，缺陷 108/109/110/111）
// 结构：子 tab ×3（今日响应 / 明日响应 / 历史响应）。
//  - 今日响应：①折线「云平台今日实时总有功功率」（实时功率/目标功率，淡黄色区块=响应时段，x 00:00–24:00）
//             ②实时状态文字 ③「响应充放电量」概览卡片 ④电站分配表（行内「查看」单站功率图）⑤已响应指令表（表头成功/失败次数）
//  - 明日响应：结果编号 / 指令模式 / 指令下发时间 / 响应时段 / 目标功率 / 倒计时
//  - 历史响应：统计分析卡片 + 响应记录表（筛选置于表格上方，含响应时长/充放电量/减少值，支持详情弹层）
// 缺陷 108：筛选放在各子 tab / 表格上方，不再堆在页面最顶部。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Button, Space, Select, DatePicker, Modal, message } from 'antd';
import ScrollTabs from '../../components/ScrollTabs';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea,
} from 'recharts';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import {
  TODAY_RESPONSE_CMD, TODAY_RESPONSE_WINDOWS, getTodayPowerCurve,
  RESPONSE_STATION_ALLOC, getStationPowerCurve,
  RESPONSE_ACTIVE_CMDS, RESPONSE_COMMANDS, TOMORROW_RESPONSE,
  RESPONSE_HISTORY_STAT, RESPONSE_HISTORY,
} from '../../config/stationData';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const tooltipStyle = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const dash = <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;
const STATE_TAG = { 成功: 'green', 失败: 'red' };

const Panel = ({ title, extra, children }) => (
  <div className="panel">
    {title ? <div className="panel-title">{title}{extra ? <small>{extra}</small> : null}</div> : null}
    {children}
  </div>
);

const KV = ({ items }) => (
  <div className="dev-kv">
    {items.map(([k, v]) => (
      <div className="dev-kv-row" key={k}>
        <span className="dev-kv-k">{k}</span>
        <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
      </div>
    ))}
  </div>
);

// 概览卡片（复刻 BlockRenderer 的 kpi-card 视觉）
const KpiCards = ({ items }) => (
  <div className="kpi-grid kpi-grid-auto">
    {items.map((k) => (
      <div className="kpi-card" key={k.label}>
        <div className="kpi-label">{k.label}</div>
        <div className="kpi-value">{k.value}{k.unit ? <span className="unit">{k.unit}</span> : null}</div>
      </div>
    ))}
  </div>
);

// ---------- 今日响应 ----------
const TodayTab = () => {
  const cmd = TODAY_RESPONSE_CMD;
  const curve = useMemo(() => getTodayPowerCurve(), []);
  const [stationChart, setStationChart] = useState(null);

  const stationColumns = [
    { title: '电站名称', dataIndex: 'station', render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '额定功率(kW)', dataIndex: 'rated' },
    { title: 'SOC', dataIndex: 'soc', render: (v) => `${v}%` },
    { title: '实时执行功率(kW)', dataIndex: 'execPower' },
    { title: '已响应放电量(kWh)', dataIndex: 'discharge' },
    { title: '已响应充电量(kWh)', dataIndex: 'charge' },
    {
      title: '功率图', key: 'op', width: 90,
      render: (_, r) => <Button type="link" size="small" onClick={() => setStationChart(r)}>查看</Button>,
    },
  ];

  const commandColumns = [
    { title: '结果编号', dataIndex: 'no', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '指令下发时间', dataIndex: 'issuedAt' },
    { title: '指令模式', dataIndex: 'mode' },
    { title: '指令结束', dataIndex: 'stage' },
    { title: '目标功率(kW)', dataIndex: 'target' },
    { title: '响应状态', dataIndex: 'state', render: (v) => (v ? <Tag color={STATE_TAG[v] || 'default'} style={{ margin: 0 }}>{v}</Tag> : dash) },
    { title: '响应时长', dataIndex: 'duration' },
    { title: '已响应放电量(kWh)', dataIndex: 'discharge' },
    { title: '已响应充电量(kWh)', dataIndex: 'charge' },
  ];

  const activeColumns = [
    { title: '结果编号', dataIndex: 'no', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '指令下发时间', dataIndex: 'issuedAt', width: 170 },
    { title: '指令模式', dataIndex: 'mode' },
    { title: '目标功率(kW)', dataIndex: 'target' },
    { title: '响应状态', dataIndex: 'state', render: (v) => <Tag color="blue" style={{ margin: 0 }}>{v}</Tag> },
    { title: '响应时长', dataIndex: 'duration' },
    { title: '已响应放电量(kWh)', dataIndex: 'discharge' },
    { title: '已响应充电量(kWh)', dataIndex: 'charge' },
  ];

  const overviewItems = [
    { label: '结果编号', value: cmd.no },
    { label: '指令下发时间', value: cmd.issuedAt },
    { label: '实时目标功率', value: cmd.targetPower, unit: 'kW' },
    { label: '实时执行总功率', value: cmd.execPower, unit: 'kW' },
    { label: '已响应放电量', value: cmd.discharge, unit: 'kWh' },
    { label: '已响应充电量', value: cmd.charge, unit: 'kWh' },
  ];

  const stationCurve = stationChart ? getStationPowerCurve(stationChart.key) : [];

  return (
    <>
      <Panel title="云平台今日实时总有功功率" extra="淡黄色区块为响应时段 · 单位 kW">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={curve} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="clock" tick={tick} axisLine={false} tickLine={false} interval={2} />
            <YAxis tick={tick} axisLine={false} tickLine={false} width={52} domain={[-70, 100]} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
            {TODAY_RESPONSE_WINDOWS.map(([s, e]) => (
              <ReferenceArea key={s} x1={s} x2={e} strokeOpacity={0} fill="#f4c542" fillOpacity={0.18} />
            ))}
            <Line dataKey="actual" name="实时功率" stroke="#00d4ff" dot={false} strokeWidth={2} />
            <Line dataKey="target" name="目标功率" stroke="#ff5a7f" dot={false} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>

      <div style={{ margin: '12px 0', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
        云平台实时状态：
        <span style={{ color: cmd.status === '响应中' ? '#52e8b0' : 'rgba(255,255,255,0.4)', fontWeight: cmd.status === '响应中' ? 600 : 400 }}>响应中</span>
        <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.3)' }}>/</span>
        <span style={{ color: cmd.status === '未进入响应' ? '#52e8b0' : 'rgba(255,255,255,0.4)', fontWeight: cmd.status === '未进入响应' ? 600 : 400 }}>未进入响应</span>
      </div>

      <Panel title="响应充放电量" extra={`当前指令 ${cmd.mode}`}>
        <KpiCards items={overviewItems} />
      </Panel>

      <Panel title="响应中指令" extra="当前正在执行的响应指令">
        <Table
          rowKey="key"
          dataSource={RESPONSE_ACTIVE_CMDS}
          columns={activeColumns}
          pagination={false}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </Panel>

      <Panel title="电站分配" extra="点击「查看」弹出单站当日功率图">
        <Table
          rowKey="key"
          dataSource={RESPONSE_STATION_ALLOC}
          columns={stationColumns}
          pagination={false}
          size="middle"
          scroll={{ x: 900 }}
        />
      </Panel>

      <Panel title="已响应指令" extra={`成功 ${RESPONSE_COMMANDS.success} 次 · 失败 ${RESPONSE_COMMANDS.fail} 次`}>
        <Table
          rowKey="key"
          dataSource={RESPONSE_COMMANDS.rows}
          columns={commandColumns}
          pagination={{ pageSize: 10, size: 'small' }}
          size="middle"
          scroll={{ x: 1100 }}
        />
      </Panel>

      <Modal
        open={!!stationChart}
        title={`单站当日功率图 · ${stationChart ? stationChart.station : ''}`}
        width={820}
        onCancel={() => setStationChart(null)}
        footer={<Button type="primary" onClick={() => setStationChart(null)}>关闭</Button>}
      >
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={stationCurve} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="clock" tick={tick} axisLine={false} tickLine={false} interval={2} />
              <YAxis tick={tick} axisLine={false} tickLine={false} width={52} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
              <Line dataKey="actual" name="实时执行功率 (kW)" stroke="#00d4ff" dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Modal>
    </>
  );
};

// ---------- 明日响应 ----------
const TomorrowTab = () => {
  const columns = [
    { title: '结果编号', dataIndex: 'no', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '指令模式', dataIndex: 'mode' },
    { title: '指令下发时间', dataIndex: 'issuedAt', width: 170 },
    { title: '响应时段', dataIndex: 'window', width: 230 },
    { title: '目标功率(kW)', dataIndex: 'target' },
    { title: '倒计时', dataIndex: 'countdown', render: (v) => <span style={{ color: '#f4c542' }}>{v}</span> },
  ];
  return (
    <Panel title="明日响应指令" extra="倒计时结束自动进入响应模式">
      <Table
        rowKey="key"
        dataSource={TOMORROW_RESPONSE}
        columns={columns}
        pagination={false}
        size="middle"
        scroll={{ x: 900 }}
      />
    </Panel>
  );
};

// ---------- 历史响应 ----------
const HistoryTab = () => {
  const [station, setStation] = useState();
  const [range, setRange] = useState(null);
  const [applied, setApplied] = useState({ station: undefined, range: null });
  const [detail, setDetail] = useState(null);

  const doQuery = () => {
    setApplied({ station, range });
    message.success('已按日期 / 电站查询');
  };
  const doReset = () => {
    setStation(undefined);
    setRange(null);
    setApplied({ station: undefined, range: null });
    message.success('筛选条件已重置');
  };

  const rows = useMemo(() => RESPONSE_HISTORY.filter((r) => {
    if (applied.station) {
      const want = RESPONSE_STATION_ALLOC.find((s) => s.key === applied.station);
      if (want && r.station !== want.station) return false;
    }
    if (applied.range && applied.range[0] && applied.range[1]) {
      const s = applied.range[0].format('YYYY-MM-DD');
      const e = applied.range[1].format('YYYY-MM-DD');
      const d = r.startAt.slice(0, 10);
      if (d < s || d > e) return false;
    }
    return true;
  }), [applied]);

  const columns = [
    { title: '结果编号', dataIndex: 'no', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '指令下发时间', dataIndex: 'issuedAt', width: 170 },
    { title: '指令模式', dataIndex: 'mode' },
    { title: '指令开始时间', dataIndex: 'startAt', width: 170 },
    { title: '响应时长(分)', dataIndex: 'duration' },
    { title: '目标功率(kW)', dataIndex: 'target' },
    { title: '减少值(kW)', dataIndex: 'reduce' },
    { title: '响应状态', dataIndex: 'state', render: (v) => <Tag color={STATE_TAG[v] || 'default'} style={{ margin: 0 }}>{v}</Tag> },
    { title: '已响应放电量(kWh)', dataIndex: 'discharge' },
    { title: '已响应充电量(kWh)', dataIndex: 'charge' },
    {
      title: '操作', key: 'op', fixed: 'right', width: 90,
      render: (_, r) => <Button type="link" size="small" onClick={() => setDetail(r)}>详情</Button>,
    },
  ];

  const statItems = [
    { label: '累计成功响应次数', value: RESPONSE_HISTORY_STAT.success, unit: '次' },
    { label: '累计响应时长', value: RESPONSE_HISTORY_STAT.duration, unit: '分钟' },
    { label: '累积充电量', value: RESPONSE_HISTORY_STAT.charge, unit: 'kWh' },
    { label: '累积放电量', value: RESPONSE_HISTORY_STAT.discharge, unit: 'kWh' },
  ];

  const d = detail || {};

  return (
    <>
      <Panel title="统计分析" extra="累计成功响应次数 / 时长 / 充放电量">
        <KpiCards items={statItems} />
      </Panel>

      <Panel title="响应记录" extra="可按日期 / 电站查询，单击详情查看指令明细">
        {/* 缺陷 108：筛选置于表格上方 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>日期</span>
            <DatePicker.RangePicker size="small" value={range} onChange={setRange} format="YYYY-MM-DD" style={{ width: 230 }} />
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>电站</span>
            <Select
              size="small"
              allowClear
              value={station}
              onChange={setStation}
              style={{ minWidth: 180 }}
              placeholder="全部电站"
              options={RESPONSE_STATION_ALLOC.map((s) => ({ value: s.key, label: s.station }))}
            />
          </span>
          <Space>
            <Button type="primary" size="small" onClick={doQuery}>查询</Button>
            <Button size="small" onClick={doReset}>重置</Button>
          </Space>
        </div>
        <Table
          rowKey="key"
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 10, size: 'small' }}
          size="middle"
          scroll={{ x: 1400 }}
        />
      </Panel>

      <Modal
        open={!!detail}
        title={`响应指令详情 · ${d.no || ''}`}
        width={720}
        onCancel={() => setDetail(null)}
        footer={<Button type="primary" onClick={() => setDetail(null)}>关闭</Button>}
      >
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-title">指令信息</div>
          <KV items={[
            ['结果编号', d.no], ['指令模式', d.mode], ['响应状态', d.state],
            ['指令下发时间', d.issuedAt], ['指令开始时间', d.startAt], ['指令结束时间', d.endAt],
            ['响应时长', d.duration != null ? `${d.duration} 分钟` : '-'],
            ['目标功率', d.target != null ? `${d.target} kW` : '-'],
            ['减少值（功率减少值）', d.reduce != null ? `${d.reduce} kW` : '-'],
          ]} />
        </div>
        <div className="panel">
          <div className="panel-title">响应充放电量</div>
          <KV items={[
            ['已响应放电量', d.discharge != null ? `${d.discharge} kWh` : '-'],
            ['已响应充电量', d.charge != null ? `${d.charge} kWh` : '-'],
            ['参与电站', d.station],
          ]} />
        </div>
      </Modal>
    </>
  );
};

const ResponsePage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/biz/response');

  // 缺陷 108：页面顶部不再堆叠筛选；筛选下钻到各子 tab / 表格上方
  const actions = [{ key: 'declare', label: '申报响应', primary: true }, { key: 'export', label: '导出' }];
  const onAction = (a) => {
    if (a.key === 'declare') message.success('已打开响应申报，可申报次日削峰 / 填谷响应');
    else message.success('已导出当前响应数据为 Excel');
  };

  const items = [
    { key: 'today', label: '今日响应', children: <TodayTab /> },
    { key: 'tomorrow', label: '明日响应', children: <TomorrowTab /> },
    { key: 'history', label: '历史响应', children: <HistoryTab /> },
  ];

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '响应管理'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={actions}
      onAction={onAction}
    >
      <div className="panel blk-tabs">
        <ScrollTabs items={items} />
      </div>
    </PageSkeleton>
  );
};

export default ResponsePage;
